import React, { useState } from 'react';
import SEO from '../components/SEO';
import styles from './RequestDemo.module.css';

const USE_CASES = ['AI Knowledge Bases','SOPs & Policy Docs','Customer Support Content','Legal & Compliance','Enterprise AI Copilots','Other'];
const TEAM_SIZES = ['1–10 employees','11–50 employees','51–200 employees','201–1000 employees','1000+ employees'];

export default function RequestDemo() {
  const [form, setForm] = useState({ firstName:'',lastName:'',email:'',company:'',jobTitle:'',teamSize:'',useCase:'',message:'' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const set = (k) => (e) => { setForm(f=>({...f,[k]:e.target.value})); setErrors(er=>({...er,[k]:''})); };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required.';
    if (!form.lastName.trim())  e.lastName  = 'Last name is required.';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'A valid work email is required.';
    if (!form.company.trim())   e.company   = 'Company name is required.';
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus('loading');
    try {
      const res = await fetch('/api/demo', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(form) });
      if (res.ok) setStatus('success');
      else throw new Error('Server error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') return (
    <div className={styles.successWrap}>
      <div className={styles.successIcon}>✅</div>
      <h2>You're booked in!</h2>
      <p>Thanks for reaching out. Our team will contact you at your work email within a few hours to confirm your demo slot.</p>
      <a href="/" className="btn btn-navy" style={{marginTop:16}}>Back to Home</a>
    </div>
  );

  return (
    <>
      <SEO title="Request a Demo" description="Book a free 30-minute ReadIQ demo tailored to your use case. Contact AI Business Solutions at sam@proautomate.ai." canonical="/request-demo" keywords="ReadIQ demo, AI document platform demo, book ReadIQ demo, AI Business Solutions"/>

      {/* HEADER */}
      <section className={styles.demoHeader}>
        <div className={`wrap ${styles.dhInner}`}>
          <div>
            <nav className={styles.crumb}><a href="/">Home</a><span>/</span><span>Request a Demo</span></nav>
            <div className={styles.kicker}><span className={styles.kickerDot}/><span>Free 30-Minute Live Demo</span></div>
            <h1>See ReadIQ<br/><em>in action</em></h1>
            <p>Watch how ReadIQ transforms your enterprise documents into GenAI-ready knowledge assets — live, with your own documents if you'd like.</p>
          </div>
          <div className={styles.trust}>
            {[['⚡','Same-day response','We reply within a few hours'],['🎯','Tailored walkthrough','Demo built around your use case'],['🔒','No commitment','No credit card or contract needed']].map(([ico,t,s])=>(
              <div key={t} className={styles.trustItem}><div className={styles.trustIco}>{ico}</div><div><strong>{t}</strong><span>{s}</span></div></div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.demoBody}>
        <div className={`wrap ${styles.demoGrid}`}>

          {/* FORM */}
          <div>
            <div className="eyebrow">Book Your Session</div>
            <h2 className="sh" style={{marginBottom:8}}>Request a personalised demo</h2>
            <p style={{fontSize:14.5,color:'var(--muted)',marginBottom:32,lineHeight:1.7}}>Fill in the form and our team will reach out to schedule a session at a time that works for you.</p>
            <div className={styles.formPanel}>
              <div className={styles.formHead}><h3>Your Details</h3><p>Takes less than 2 minutes — we'll handle the rest.</p></div>
              <form className={styles.formBody} onSubmit={submit} noValidate>
                <div className={styles.formRow}>
                  <Field label="First Name" req value={form.firstName} onChange={set('firstName')} placeholder="e.g. Sarah" error={errors.firstName}/>
                  <Field label="Last Name"  req value={form.lastName}  onChange={set('lastName')}  placeholder="e.g. Johnson" error={errors.lastName}/>
                </div>
                <Field label="Work Email" req type="email" value={form.email} onChange={set('email')} placeholder="you@company.com" error={errors.email}/>
                <div className={styles.formRow}>
                  <Field label="Company Name" value={form.company} onChange={set('company')} placeholder="Your organisation" error={errors.company}/>
                  <Field label="Job Title" value={form.jobTitle} onChange={set('jobTitle')} placeholder="e.g. Head of AI"/>
                </div>
                <div className={styles.formRow}>
                  <Field label="Team Size" type="select" value={form.teamSize} onChange={set('teamSize')} options={TEAM_SIZES}/>
                  <Field label="Primary Use Case" type="select" value={form.useCase} onChange={set('useCase')} options={USE_CASES}/>
                </div>
                <Field label="Tell us about your document challenge" type="textarea" value={form.message} onChange={set('message')} placeholder="What's the main problem you're trying to solve?"/>
                <div className={styles.divider}/>
                <button type="submit" className={styles.submitBtn} disabled={status==='loading'}>
                  {status==='loading' ? 'Sending…' : '→ Book My Free Demo'}
                </button>
                {status==='error' && <p style={{color:'var(--red)',textAlign:'center',marginTop:10,fontSize:13}}>Something went wrong — please try again.</p>}
                <p className={styles.formNote}>By submitting you agree to our Privacy Policy. We'll never share your details with third parties.</p>
              </form>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className={styles.sidebar}>
            <div className={styles.contactCard}>
              <div className={styles.ccEyebrow}>Get In Touch</div>
              <h3>AI Business Solutions</h3>
              <p>Enterprise AI platform specialists — here to help.</p>
              <div className={styles.ccRows}>
                {[['✉️','Email Us',<a href="mailto:sam@proautomate.ai">sam@proautomate.ai</a>],['📍','Office Address',<>1307 White Horse Rd<br/>Voorhees, NJ 08043</>],['🕐','Business Hours','Mon – Fri, 9 AM – 6 PM EST']].map(([ico,lbl,val])=>(
                  <div key={lbl} className={styles.ccRow}><div className={styles.ccIco}>{ico}</div><div><div className={styles.ccLbl}>{lbl}</div><div className={styles.ccVal}>{val}</div></div></div>
                ))}
              </div>
              <div className={styles.ccFooter}>Prefer to reach out directly? Email <a href="mailto:sam@proautomate.ai">sam@proautomate.ai</a> and we'll get back to you same day.</div>
            </div>

            <div className={styles.expectCard}>
              <h4>What happens next?</h4>
              {[['We confirm your slot','You\'ll hear from us within a few hours with available time slots.'],['We tailor the demo','We prepare a walkthrough focused on your specific use case.'],['Live 30-minute session','See ReadIQ score, improve, and classify real documents.'],['Tailored follow-up','We\'ll send a summary with pricing options that fit your scale.']].map(([t,d],i)=>(
                <div key={t} className={styles.expectItem}><div className={styles.expectStep}>{i+1}</div><div><strong>{t}</strong><span>{d}</span></div></div>
              ))}
            </div>

            <div className={styles.proofCard}>
              <strong>Trusted by enterprise teams</strong>
              <p>ReadIQ delivers measurable impact from day one.</p>
              <div className={styles.proofStats}>
                {[['70%','Less review effort'],['12','Quality dimensions'],['0–100','AI Friendliness Score']].map(([n,l])=>(
                  <div key={l}><div className={styles.proofNum}>{n}</div><div className={styles.proofLbl}>{l}</div></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, req, type='text', value, onChange, placeholder, error, options }) {
  const baseStyle = { width:'100%', fontFamily:'Plus Jakarta Sans, sans-serif', fontSize:14, color:'var(--ink)', background:'var(--white)', border:`1.5px solid ${error?'var(--red)':'var(--border2)'}`, borderRadius:'var(--r-sm)', padding:'10px 14px', outline:'none', transition:'border-color .18s, box-shadow .18s', boxSizing:'border-box', boxShadow: error?'0 0 0 3px rgba(239,68,68,.1)':undefined };
  return (
    <div style={{marginBottom:20}}>
      <label style={{display:'block',fontSize:12.5,fontWeight:700,color:'var(--ink2)',marginBottom:7}}>
        {label}{req && <span style={{color:'var(--blue)',marginLeft:2}}>*</span>}
      </label>
      {type==='select' ? (
        <select value={value} onChange={onChange} style={{...baseStyle,cursor:'pointer',WebkitAppearance:'none'}}>
          <option value="" disabled>Select {label.toLowerCase()}</option>
          {options.map(o=><option key={o}>{o}</option>)}
        </select>
      ) : type==='textarea' ? (
        <textarea value={value} onChange={onChange} placeholder={placeholder} rows={4} style={{...baseStyle,resize:'vertical'}}/>
      ) : (
        <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={baseStyle}/>
      )}
      {error && <div style={{color:'var(--red)',fontSize:12,marginTop:4}}>{error}</div>}
    </div>
  );
}
